import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Bot,
  Users as UsersIcon,
  Activity,
  CalendarDays,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '../lib/utils';
import Button from '../components/Button';
import ProgressBar from '../components/AnalyticsComponents/ProgressBar';
import Input from '../components/Input';
import userService from '../services/userService';
import agentService from '../services/agentService';
import teamService from '../services/teamService';
import Loaders from '../components/Loaders';
import sessionsService from '../services/sessionsServices';
import Field from '../components/Field';
import adminDashboardService from '../services/adminDashboardService';
import Skeleton from 'react-loading-skeleton';
import { queryKeys } from '../hooks';

const ITEMS_PER_PAGE = 6;

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(1);
  const [agentData, setAgentData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [sessionsData, setSessionsData] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionDateFrom, setSessionDateFrom] = useState('');
  const [sessionDateTo, setSessionDateTo] = useState('');
  const [sessionPlatformFilter, setSessionPlatformFilter] = useState('');
  const [sessionPlatformValue, setSessionPlatformValue] = useState({ label: 'All', value: '' });
  const { users_details_data, users_details_loading } = userService?.GetUserDetails(id);
  const [platforms, setPlatforms] = useState([{ label: 'All', value: '' }]);

  const { platforms_data } = adminDashboardService?.GetPlatforms();

  // One cached query per unique agent id; React Query dedupes re-renders,
  // StrictMode double-mounts, and repeat visits to this page.
  const uniqueAgentIds = useMemo(
    () => [...new Set((sessionsData || []).map(s => s?.agent_id).filter(Boolean))],
    [sessionsData],
  );

  const agentDetailQueries = useQueries({
    queries: uniqueAgentIds.map(agentId => ({
      queryKey: queryKeys.agents.detail(agentId),
      queryFn: () => agentService.getAgentDetail(agentId),
      staleTime: Infinity,
      retry: false,
    })),
  });

  const isAgentsLoading = agentDetailQueries.some(q => q.isPending);
  const agentsMap = useMemo(
    () =>
      agentDetailQueries.filter(q => q.data?.success && q.data?.detail).map(q => q.data.detail),
    [agentDetailQueries],
  );

  useEffect(() => {
    if (sessionsData && platforms_data?.platforms) {
      const activePlatformIds = new Set(sessionsData.map(session => session.platform_id));

      const formattedPlatforms = platforms_data.platforms
        .filter(p => activePlatformIds.has(p.platform_id))
        .map(p => ({
          label: p.platform_id.charAt(0).toUpperCase() + p.platform_id.slice(1),
          value: p.platform_id,
        }));

      setPlatforms([{ label: 'All', value: '' }, ...formattedPlatforms]);
    }
  }, [platforms_data, sessionsData]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (users_details_data?.detail?.email) {
        try {
          const data = await agentService.getAllAgentsOfUser({
            user_id: users_details_data?.detail?.email,
          });
          const teamData = await teamService.getAllTeamsOfUser(users_details_data?.detail?.email);

          setSessionsLoading(true);

          const sessionsData = await sessionsService.getSessionsListOfUser({
            user_id: users_details_data?.detail?.email,
          });

          setAgentData(data);
          setTeamData(teamData);
          setSessionsData([
            ...sessionsData?.sessions?.Favorite,
            ...sessionsData?.sessions?.Today,
            ...sessionsData?.sessions?.Yesterday,
            ...sessionsData?.sessions['Last 7 Days'],
            ...sessionsData?.sessions['Last 30 Days'],
          ]);
          setSessionsLoading(false);
        } catch (error) {
          console.error('Failed to fetch agents:', error);
        }
      }
    };

    fetchAgents();
    // Key on the email value, not the response object reference, so this
    // doesn't re-run (and refetch everything) on every identical response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users_details_data?.detail?.email]);

  if (users_details_loading) {
    return <Loaders viewLoader />;
  }

  if (!users_details_data?.detail) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">User not found</p>
      </div>
    );
  }

  // Filter sessions
  const filteredSessions = sessionsData?.filter(s => {
    const matchesPlatform = s?.platform_id?.includes(sessionPlatformFilter);
    const sessionDate = new Date(s?.created_at);
    const matchesFrom = !sessionDateFrom || sessionDate >= new Date(sessionDateFrom);
    const matchesTo = !sessionDateTo || sessionDate <= new Date(sessionDateTo + 'T23:59:59Z');
    return matchesPlatform && matchesFrom && matchesTo;
  });

  const totalPages = Math.ceil(filteredSessions?.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredSessions?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const getPaginationRange = (current, total) => {
    const delta = 2; // How many pages to show on each side of current
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back link */}
      <Link
        to="/users"
        className="flex items-center gap-2 text-sm text-ink text-muted-foreground hover:text-foreground transition-colors w-max">
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </Link>

      {/* Profile Header */}
      <div className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-xl bg-[#721b1f] flex items-center justify-center capitalize text-2xl font-bold text-white shrink-0">
            {users_details_data?.detail?.first_name?.slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-bold text-foreground">
                {users_details_data?.detail?.first_name} {users_details_data?.detail?.last_name}
              </h2>
            </div>
            <div className="flex items-center gap-5 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                {users_details_data?.detail?.email}
              </span>
              {/* <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Joined {user?.joinedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Last seen {user?.lastSeen}
              </span> */}
              <span className="capitalize">{users_details_data?.detail?.platform}</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6 pt-6 border-t border-[rgba(157,157,157,0.3)]">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Requests</p>
            <p className="text-lg font-bold text-foreground tabular-nums">
              {users_details_data?.detail?.plan?.request_limit}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Sessions</p>
            <p className="text-lg font-bold text-foreground">{sessionsData?.length ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">API Calls</p>
            <p className="text-lg font-bold text-foreground tabular-nums">
              <span className="text-success">{users_details_data?.detail?.usage?.api_calls}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Agents</p>
            <p className="text-lg font-bold text-foreground">{agentData?.agents?.length ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Teams</p>
            <p className="text-lg font-bold text-foreground">{teamData?.teams?.length ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Plan</p>
            <p className="text-lg font-bold text-[#721b1f] capitalize">{users_details_data?.detail?.plan?.type}</p>
          </div>
        </div>

        {/* Quota */}
        <div className="mt-4 pt-4 border-t border-[rgba(157,157,157,0.3)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Quota Usage</span>
            <span className="text-xs font-medium text-foreground">
              {users_details_data?.detail?.usage?.api_calls} / {users_details_data?.detail?.plan?.request_limit}
            </span>
          </div>
          <ProgressBar
            usedCr={users_details_data?.detail?.usage?.api_calls}
            totalCr={users_details_data?.detail?.plan?.request_limit}
            color="#721b1f"
            bg="#2a2a2a"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#19191b] rounded-lg w-max border border-[rgba(157,157,157,0.3)] p-2 h-auto flex gap-2 flex-wrap">
        <div
          value="sessions"
          className={`text-sm data-[state=active]:bg-primary rounded-md cursor-pointer data-[state=active]:text-primary-foreground px-3 py-1 ${activeTab === 1 && 'bg-[#721b1f] text-white'}`}
          onClick={() => setActiveTab(1)}>
          Sessions
        </div>
        <div
          value="agents"
          className={`text-sm data-[state=active]:bg-primary rounded-md cursor-pointer data-[state=active]:text-primary-foreground px-3 py-1 ${activeTab === 2 && 'bg-[#721b1f] text-white'}`}
          onClick={() => setActiveTab(2)}>
          Agents
        </div>
        <div
          value="teams"
          className={`text-sm data-[state=active]:bg-primary rounded-md cursor-pointer data-[state=active]:text-primary-foreground px-3 py-1 ${activeTab === 3 && 'bg-[#721b1f] text-white'}`}
          onClick={() => setActiveTab(3)}>
          Teams
        </div>
      </div>

      {/* Sessions Tab */}
      <div className={activeTab === 1 ? 'block' : 'hidden'}>
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarDays className="w-3.5 h-3.5" />
              <span>Filter:</span>
            </div>
            <Input
              type="date"
              value={sessionDateFrom}
              onChange={e => setSessionDateFrom(e.target.value)}
              className="!w-[160px] h-8 text-xs bg-muted/30 border-[rgba(157,157,157,0.3)] 
             [&::-webkit-calendar-picker-indicator]:invert"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={sessionDateTo}
              onChange={e => setSessionDateTo(e.target.value)}
              className="!w-[160px] h-8 text-xs bg-muted/30 border-[rgba(157,157,157,0.3)] 
             [&::-webkit-calendar-picker-indicator]:invert"
            />

            <div className="w-max">
              <Field
                type="select"
                name="platform"
                placeholder="Select Platform"
                options={platforms}
                value={sessionPlatformValue}
                className="!w-[170px]"
                noMargin
                onChange={e => {
                  setSessionPlatformFilter(e?.target?.value?.value);
                  setSessionPlatformValue(e?.target?.value);
                }}
              />
            </div>

            {(sessionDateFrom || sessionDateTo || sessionPlatformFilter !== '') && (
              <Button
                width="100px"
                onClick={() => {
                  setSessionDateFrom('');
                  setSessionDateTo('');
                  setSessionPlatformFilter('');
                  setSessionPlatformValue({ label: 'All', value: '' });
                }}>
                Clear
              </Button>
            )}
            <span className="ml-auto text-sm">
              {filteredSessions?.length ?? 0} session{filteredSessions?.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Sessions list */}
          {sessionsLoading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl overflow-hidden mb-4">
                  <div className="w-full flex items-center justify-between p-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="size-10 shrink-0">
                        <Skeleton height={40} width={40} borderRadius={8} baseColor="#202020" highlightColor="#444" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <div className="w-32">
                            <Skeleton height={18} baseColor="#202020" highlightColor="#444" />
                          </div>
                          <div className="w-16">
                            <Skeleton height={20} borderRadius={6} baseColor="#202020" highlightColor="#444" />
                          </div>
                        </div>

                        <div className="flex items-center gap-3 mt-0.5">
                          <div className="w-20">
                            <Skeleton height={14} baseColor="#202020" highlightColor="#444" />
                          </div>
                          <span className="text-muted-foreground">·</span>
                          <div className="w-16">
                            <Skeleton height={14} baseColor="#202020" highlightColor="#444" />
                          </div>
                          <span className="text-muted-foreground">·</span>
                          <div className="w-24">
                            <Skeleton height={14} baseColor="#202020" highlightColor="#444" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <div className="text-right hidden sm:block">
                        <div className="w-12 ml-auto mb-1">
                          <Skeleton height={10} baseColor="#202020" highlightColor="#444" />
                        </div>
                        <div className="w-28">
                          <Skeleton height={14} baseColor="#202020" highlightColor="#444" />
                        </div>
                      </div>
                      <div className="w-4 h-4 rounded-full">
                        <Skeleton circle height={16} width={16} baseColor="#202020" highlightColor="#444" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
          ) : (
            <>
              {filteredSessions?.length === 0 ? (
                <div className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-8 text-center">
                  <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No sessions found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedUsers?.map(session => (
                    <div
                      key={session?.id}
                      className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl overflow-hidden card-hover hover:border-[#86a30a]"
                      onClick={() => {
                        navigate(`/chat/${session?.id}`);
                      }}>
                      {/* Session header - clickable */}
                      <button className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {isAgentsLoading ? (
                            <div className="size-10 shrink-0">
                              <Skeleton
                                height={40}
                                width={40}
                                borderRadius={8}
                                baseColor="#202020"
                                highlightColor="#444"
                              />
                            </div>
                          ) : (
                            <img
                              src={agentsMap?.find(a => a.agent_id === session?.agent_id)?.icon}
                              alt="icon"
                              className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 object-cover"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-foreground truncate">{session?.title}</p>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-sm text-muted-foreground flex-wrap">
                              <span className="capitalize">
                                {agentsMap?.find(a => a.agent_id === session?.agent_id)?.agent_name || 'N/A'}
                              </span>
                              <span>·</span>
                              <span className="flex items-center capitalize gap-1">{session?.platform_id}</span>
                              <span>·</span>
                              <span>{new Date(session?.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <div className="text-right hidden sm:block">
                            <p className="text-xs text-muted-foreground">Updated</p>
                            <p className="text-sm text-foreground">
                              {new Date(session?.updated_at).toLocaleTimeString()}{' '}
                              {new Date(session?.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-5 py-3 rounded-xl bg-[#19191b] border border-[rgba(157,157,157,0.3)]">
                    <span className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {getPaginationRange(page, totalPages).map((p, index) => {
                        if (p === '...') {
                          return (
                            <span key={`dots-${index}`} className="px-2 text-muted-foreground text-xs">
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={cn(
                              'w-7 h-7 rounded-md text-xs font-medium transition-colors',
                              p === page
                                ? 'bg-[#721b1f] text-white'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                            )}>
                            {p}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Agents Tab */}
      <div className={activeTab === 2 ? 'block' : 'hidden'}>
        <div className="space-y-3">
          {agentData?.agents?.length === 0 ? (
            <div className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-8 text-center">
              <Bot className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No agents assigned</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {agentData?.agents?.map(agent => (
                <div
                  key={agent?.id}
                  className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-4 card-hover">
                  <div className="flex items-start gap-3">
                    <img
                      src={agent?.icon}
                      alt="icon"
                      className="size-14 rounded-lg bg-primary/10 flex items-center justify-center text-xl shrink-0 object-cover"
                    />
                    <div className="min-w-0">
                      <p className="text-lg font-semibold text-foreground capitalize mb-1">{agent?.agent_name}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{agent?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Teams Tab */}
      <div className={activeTab === 3 ? 'block' : 'hidden'}>
        <div className="space-y-3">
          {teamData?.teams?.length === 0 ? (
            <div className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-8 text-center">
              <UsersIcon className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No team memberships</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {teamData?.teams?.map(team => (
                <div
                  key={team?.team_id}
                  className="bg-[#19191b] border border-[rgba(157,157,157,0.3)] rounded-xl p-4 card-hover">
                  <div className="flex items-start gap-3">
                    <img
                      src={team?.icon}
                      alt="icon"
                      className="size-14 rounded-lg bg-primary/10 flex items-center justify-center text-xl shrink-0 object-cover"
                    />
                    <div className="min-w-0">
                      <p className="text-lg font-semibold text-foreground capitalize mb-1">{team?.team_name}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{team?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
